import type { TypedSupabaseClient } from '../client';
import type {
  ICaisseRepository,
  CashSession,
  CashMovement,
  CashSessionFilters,
  OpenCashSessionInput,
  CloseCashSessionInput,
  CreateCashMovementInput,
} from '../../../interfaces/src';

export class SupabaseCaisseRepository implements ICaisseRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async listSessions(filters?: CashSessionFilters): Promise<CashSession[]> {
    let query = this.supabase.from('cash_sessions').select('*');

    if (filters?.bankAccountId) query = query.eq('bank_account_id', filters.bankAccountId);
    if (filters?.isClosed !== undefined) query = query.eq('is_closed', filters.isClosed);
    if (filters?.dateFrom) query = query.gte('opened_at', filters.dateFrom);
    if (filters?.dateTo) query = query.lte('opened_at', filters.dateTo);

    const { data, error } = await query.order('opened_at', { ascending: false });
    if (error) throw error;
    return (data ?? []).map(mapSession);
  }

  async getSessionById(id: string): Promise<CashSession | null> {
    const { data, error } = await this.supabase
      .from('cash_sessions')
      .select('*')
      .eq('id', id)
      .single();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return mapSession(data);
  }

  async getOpenSession(bankAccountId: string): Promise<CashSession | null> {
    const { data, error } = await this.supabase
      .from('cash_sessions')
      .select('*')
      .eq('bank_account_id', bankAccountId)
      .eq('is_closed', false)
      .maybeSingle();
    if (error) throw error;
    return data ? mapSession(data) : null;
  }

  async listOpenSessions(): Promise<CashSession[]> {
    const { data, error } = await this.supabase
      .from('cash_sessions')
      .select('*')
      .eq('is_closed', false)
      .order('opened_at', { ascending: false });
    if (error) throw error;
    return (data ?? []).map(mapSession);
  }

  async openSession(input: OpenCashSessionInput): Promise<CashSession> {
    const number = await this.getNextSessionNumber();

    const { data: session, error } = await this.supabase
      .from('cash_sessions')
      .insert({
        number,
        bank_account_id: input.bankAccountId,
        opened_by: input.openedBy ?? null,
        opening_balance: input.openingBalance,
        notes: input.notes ?? null,
      } as never)
      .select()
      .single();
    if (error) {
      if (isUniqueViolation(error, 'uq_cash_sessions_one_open_per_drawer')) {
        throw new Error('caisse.errors.alreadyOpen');
      }
      throw error;
    }

    const mvtNumber = await this.getNextMovementNumber();
    const { error: mvtError } = await this.supabase
      .from('cash_movements')
      .insert({
        number: mvtNumber,
        session_id: (session as { id: string }).id,
        movement_type: 'opening',
        amount: input.openingBalance,
        description: 'Ouverture de caisse',
        created_by: input.openedBy ?? null,
      } as never);
    if (mvtError) throw mvtError;

    return mapSession(session);
  }

  async closeSession(input: CloseCashSessionInput): Promise<CashSession> {
    const updatePayload: Record<string, unknown> = {
      is_closed: true,
      closed_by: input.closedBy ?? null,
      closed_at: new Date().toISOString(),
      expected_balance: input.expectedBalance,
      counted_balance: input.countedBalance,
    };
    if (input.notes !== undefined) updatePayload['notes'] = input.notes;

    const { data: session, error } = await this.supabase
      .from('cash_sessions')
      .update(updatePayload as never)
      .eq('id', input.sessionId)
      .eq('is_closed', false)
      .select()
      .single();
    if (error) {
      if (error.code === 'PGRST116') throw new Error('caisse.errors.sessionAlreadyClosed');
      throw error;
    }

    const mvtNumber = await this.getNextMovementNumber();
    const difference = (session as { difference: number | null }).difference ?? 0;
    const { error: mvtError } = await this.supabase
      .from('cash_movements')
      .insert({
        number: mvtNumber,
        session_id: (session as { id: string }).id,
        movement_type: 'closing',
        amount: input.countedBalance,
        description: `Fermeture — écart ${difference}`,
        created_by: input.closedBy ?? null,
      } as never);
    if (mvtError) throw mvtError;

    return mapSession(session);
  }

  async cancelEmptySession(id: string): Promise<void> {
    const { count, error: countError } = await this.supabase
      .from('cash_movements')
      .select('*', { count: 'exact', head: true })
      .eq('session_id', id)
      .neq('movement_type', 'opening');
    if (countError) throw countError;
    if ((count ?? 0) > 0) throw new Error('caisse.errors.cannotCancelWithMovements');

    const { error } = await this.supabase
      .from('cash_sessions')
      .delete()
      .eq('id', id)
      .eq('is_closed', false);
    if (error) throw error;
  }

  async listMovements(sessionId: string): Promise<CashMovement[]> {
    const { data, error } = await this.supabase
      .from('cash_movements')
      .select('*')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return (data ?? []).map(mapMovement);
  }

  async createMovement(input: CreateCashMovementInput): Promise<CashMovement> {
    const { data: sess, error: sessError } = await this.supabase
      .from('cash_sessions')
      .select('is_closed')
      .eq('id', input.sessionId)
      .single();
    if (sessError) throw sessError;
    if ((sess as { is_closed: boolean }).is_closed) throw new Error('caisse.errors.sessionClosed');

    const number = await this.getNextMovementNumber();
    const { data, error } = await this.supabase
      .from('cash_movements')
      .insert({
        number,
        session_id: input.sessionId,
        movement_type: input.movementType,
        category: input.category,
        amount: input.amount,
        description: input.description ?? null,
        created_by: input.createdBy ?? null,
      } as never)
      .select()
      .single();
    if (error) throw error;
    return mapMovement(data);
  }

  async getNextSessionNumber(): Promise<string> {
    return this.nextNumber('seq_cash_session', 'CAI');
  }

  async getNextMovementNumber(): Promise<string> {
    return this.nextNumber('seq_cash_movement', 'MVT');
  }

  private async nextNumber(seqName: string, prefix: string): Promise<string> {
    const year = new Date().getFullYear();
    const { data, error } = await this.supabase.rpc('get_next_sequence_value', {
      seq_name: seqName,
    });
    if (!error && data != null) {
      return `${prefix}-${year}-${String(data).padStart(4, '0')}`;
    }
    const table = seqName === 'seq_cash_session' ? 'cash_sessions' : 'cash_movements';
    const { count } = await this.supabase
      .from(table)
      .select('*', { count: 'exact', head: true });
    const next = (count ?? 0) + 1;
    return `${prefix}-${year}-${String(next).padStart(4, '0')}`;
  }
}

function isUniqueViolation(err: unknown, constraintName: string): boolean {
  const e = err as { code?: unknown; message?: unknown; details?: unknown };
  if (e.code !== '23505') return false;
  const text = `${String(e.message ?? '')} ${String(e.details ?? '')}`;
  return text.includes(constraintName);
}

function mapSession(row: Record<string, unknown>): CashSession {
  return {
    id: row['id'] as string,
    number: row['number'] as string,
    bankAccountId: row['bank_account_id'] as string,
    openedBy: row['opened_by'] as string,
    openedAt: row['opened_at'] as string,
    openingBalance: Number(row['opening_balance'] ?? 0),
    closedBy: (row['closed_by'] as string) ?? null,
    closedAt: (row['closed_at'] as string) ?? null,
    expectedBalance: row['expected_balance'] != null ? Number(row['expected_balance']) : null,
    countedBalance: row['counted_balance'] != null ? Number(row['counted_balance']) : null,
    difference: row['difference'] != null ? Number(row['difference']) : null,
    isClosed: (row['is_closed'] as boolean) ?? false,
    notes: (row['notes'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

function mapMovement(row: Record<string, unknown>): CashMovement {
  return {
    id: row['id'] as string,
    number: row['number'] as string,
    sessionId: row['session_id'] as string,
    movementType: row['movement_type'] as CashMovement['movementType'],
    category: (row['category'] as string) ?? null,
    amount: Number(row['amount'] ?? 0),
    description: (row['description'] as string) ?? null,
    paymentId: (row['payment_id'] as string) ?? null,
    createdBy: (row['created_by'] as string) ?? null,
    createdAt: row['created_at'] as string,
  };
}
